import { useEffect, useRef, useState, type ReactNode } from "react";

interface DeferredSectionProps {
  children: ReactNode;
  /** Min-height placeholder so the page doesn't jump while waiting. */
  minHeight?: number | string;
  /** rootMargin for IntersectionObserver — render slightly before scroll-in. */
  rootMargin?: string;
  /** Hard fallback delay (ms) so SSR/no-IO browsers still render. */
  fallbackMs?: number;
}

/**
 * Defers mounting of a section until it's near the viewport (or after a
 * fallback delay). Keeps below-the-fold queries / framer-motion / heavy
 * children out of the critical render path.
 */
export const DeferredSection = ({
  children,
  minHeight = 200,
  rootMargin = "300px 0px",
  fallbackMs = 2500,
}: DeferredSectionProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return;

    if (typeof IntersectionObserver === "undefined") {
      const t = window.setTimeout(() => setShow(true), fallbackMs);
      return () => window.clearTimeout(t);
    }

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);

    const fallback = window.setTimeout(() => setShow(true), fallbackMs);

    return () => {
      io.disconnect();
      window.clearTimeout(fallback);
    };
  }, [show, rootMargin, fallbackMs]);

  return (
    <div ref={ref} style={!show ? { minHeight } : undefined}>
      {show ? children : null}
    </div>
  );
};

export default DeferredSection;
